/**
 * Left-centre loot / pickup feed: one compact row per gain ("Gold ×12", "Hero XP ×30", a boss's
 * name), newest at the bottom, each fading out after a few seconds. Kill rewards arrive through
 * `progressFeed.kill` in the same frame their floating "+N gold" pops. Item drops are rolled by
 * the end-of-run chest (shown on the loot reveal), so the in-run feed carries currencies and kills.
 *
 * Rows are pooled (fixed count); a row's fade is a CSS animation started by a class swap, so the
 * per-frame update only expires rows (no layout reads, no allocation).
 */
import type { ContentRegistry } from '@keystone/sim';
import { el, setClass, setStyle, setText } from '../dom';
import { groupNumber } from '../kit';
import { currencyGlyphSvg } from '../../meta/ui/glyphs';
import type { HudUpdate } from '../hud';
import type { HudWidget } from '../widgets';
import { type FeedKill, progressFeed } from './progressFeed';

/** Max rows on screen. */
export const FEED_ROWS = 6;
/** A row's lifetime (the CSS fade covers the last part of it). */
export const FEED_LIFE_MS = 4200;
/** Gains of the same kind within this window of the row's last gain merge into it ("Gold ×7" → "Gold ×19"). */
export const FEED_MERGE_MS = 1200;

export type FeedKind = 'gold' | 'xp' | 'boss' | 'level';

/** Pure feed state (unit-tested): what rows to show for a stream of gains. */
export interface FeedEntry {
  kind: FeedKind;
  label: string;
  amount: number;
  at: number;
  /** Bumped when the entry changes (merge), so the view can restart its pop. */
  version: number;
}

export class FeedModel {
  readonly entries: FeedEntry[] = [];

  push(kind: FeedKind, label: string, amount: number, now: number): FeedEntry {
    // Merge into the newest row of the same kind that is still fresh (a pull's kills add up into
    // one "Gold ×N" and one "Hero XP ×N" row instead of alternating rows per kill).
    if (kind === 'gold' || kind === 'xp') {
      for (let i = this.entries.length - 1; i >= 0; i--) {
        const e = this.entries[i]!;
        if (now - e.at > FEED_MERGE_MS) break;
        if (e.kind !== kind) continue;
        e.amount += amount;
        e.at = now;
        e.version++;
        return e;
      }
    }
    const e: FeedEntry = { kind, label, amount, at: now, version: 0 };
    this.entries.push(e);
    if (this.entries.length > FEED_ROWS) this.entries.shift();
    return e;
  }

  kill(k: FeedKill, now: number, bossName: string | null): void {
    if (k.boss && bossName) this.push('boss', bossName, 0, now);
    if (k.gold > 0) this.push('gold', 'Gold', k.gold, now);
    if (k.xp > 0) this.push('xp', 'Hero XP', k.xp, now);
  }

  /** Drop expired rows; true if anything changed. */
  expire(now: number): boolean {
    let n = 0;
    while (n < this.entries.length && now - this.entries[n]!.at > FEED_LIFE_MS) n++;
    if (n) this.entries.splice(0, n);
    return n > 0;
  }
}

interface RowEls {
  root: HTMLElement;
  icon: HTMLElement;
  label: HTMLElement;
  amount: HTMLElement;
  entry: FeedEntry | null;
  version: number;
  flip: boolean;
}

const KIND_ICON: Record<FeedKind, () => Node> = {
  gold: () => currencyGlyphSvg('gold'),
  xp: () => currencyGlyphSvg('xp'),
  boss: () => currencyGlyphSvg('rating'),
  level: () => currencyGlyphSvg('xp'),
};

export class LootFeed implements HudWidget {
  readonly root: HTMLElement;
  readonly model = new FeedModel();
  private readonly rows: RowEls[] = [];
  private content: Pick<ContentRegistry, 'npc'> | null = null;
  private dirty = false;
  private readonly unsubscribe: () => void;

  constructor(host: HTMLElement) {
    this.root = el('div', 'hk-feed', host);
    this.root.setAttribute('aria-live', 'polite');
    for (let i = 0; i < FEED_ROWS; i++) {
      const root = el('div', 'hk-feed-row hk-hidden', this.root);
      const icon = el('span', 'hk-feed-icon', root);
      const label = el('span', 'hk-feed-label', root);
      const amount = el('span', 'hk-feed-amount hk-num', root);
      this.rows.push({ root, icon, label, amount, entry: null, version: -1, flip: false });
    }
    this.unsubscribe = progressFeed.subscribe({
      kill: (k, now) => {
        const npc = this.content?.npc(k.npcId);
        this.model.kill(k, now, k.boss ? (npc?.name ?? 'Boss') : null);
        this.dirty = true;
      },
      levelUp: (level, now) => {
        this.model.push('level', `Level ${level}`, 0, now);
        this.dirty = true;
      },
    });
  }

  update(h: HudUpdate, realNow: number): void {
    this.content = h.content;
    if (this.model.expire(realNow)) this.dirty = true;
    if (!this.dirty) return;
    this.dirty = false;
    const es = this.model.entries;
    // Bottom-aligned: the last entry goes in the last row.
    const off = FEED_ROWS - es.length;
    for (let i = 0; i < FEED_ROWS; i++) {
      const r = this.rows[i]!;
      const e = i >= off ? es[i - off]! : null;
      setClass(r.root, 'hk-hidden', !e);
      if (!e) {
        r.entry = null;
        continue;
      }
      if (r.entry !== e || r.version !== e.version) {
        if (r.entry?.kind !== e.kind) {
          r.icon.replaceChildren(KIND_ICON[e.kind]());
          r.root.dataset['kind'] = e.kind;
        }
        r.entry = e;
        r.version = e.version;
        setText(r.label, e.label);
        setText(r.amount, e.amount > 0 ? `×${groupNumber(e.amount)}` : '');
        // Restart the life animation (pop in, hold, fade) without a reflow, at the entry's age (a
        // row that only moved up keeps its phase; a merged row pops again).
        setStyle(r.root, 'animation-delay', `${-Math.min(FEED_LIFE_MS, Math.max(0, realNow - e.at)) | 0}ms`);
        r.flip = !r.flip;
        setClass(r.root, 'live-a', r.flip);
        setClass(r.root, 'live-b', !r.flip);
      }
    }
  }

  dispose(): void {
    this.unsubscribe();
  }
}
